import type { TypeSelectOption } from '@thefarmersfront/kitchen';

/**
 * 상품관리 메뉴별 필터 조회를 위한 api 호출시 사용하는 코드 정의
 * 호출 api: https://lip-api.dev.kurly.services/api-lip/v1/codes/menu
 * 코드 정의: https://docs.google.com/spreadsheets/d/10pzvCCl95uw_KQLQMUThOTLqe05B1V3631qf3qhZRsM/edit?gid=0#gid=0
 */

export const PRODUCT_MENU_CODE = {
  SKU_REG: 'SKU_REG',
  PROD_SKU: 'PROD_SKU',
  SKU_MAPPING: 'SKU_MAPPING',
};

export type TypeProductMenuCode = keyof typeof PRODUCT_MENU_CODE;

/**
 * 상품 관리에서 사용되는 페이지네이션 값들
 */
export const INITIAL_PRODUCT_PAGE_SIZE = 50;
export const INITIAL_PRODUCT_PAGE_OFFSET = 0;

export const PRODUCT_PAGE_SIZE = {
  50: {
    value: 50,
    text: '50개',
  },
  100: {
    value: 100,
    text: '100개',
  },
  200: {
    value: 200,
    text: '200개',
  },
} as const;

export const OPTION_NONE = { value: '', text: '' } as const;

/**
 * 상품관리 > 상품 등록/수정 페이지에서 키워드 옵션에 해당하는 필드명
 */
export const PRODUCT_REGISTER_KEYWORD_FIELD_NAME = {
  PRODUCT_NAME: 'productName',
  PRODUCT_CODE: 'productCode',
};

/**
 * 상품관리 > 상품 등록/수정 페이지에서 키워드 필드 선택에 사용하기 위한 옵션 값
 */
export const PRODUCT_REGISTER_KEYWORD_OPTION: { [id: string]: TypeSelectOption } = {
  [PRODUCT_REGISTER_KEYWORD_FIELD_NAME.PRODUCT_NAME]: {
    value: PRODUCT_REGISTER_KEYWORD_FIELD_NAME.PRODUCT_NAME,
    text: '상품명',
  },
  [PRODUCT_REGISTER_KEYWORD_FIELD_NAME.PRODUCT_CODE]: {
    value: PRODUCT_REGISTER_KEYWORD_FIELD_NAME.PRODUCT_CODE,
    text: '상품 코드',
  },
};

export type TypeProductPageSize = keyof typeof PRODUCT_PAGE_SIZE;

/**
 * 상품관리 > 상품 등록/수정 모달의 validation 제한값
 */
export const PRODUCT_LIMITS = {
  /** 숫자 입력 제한값 (최대 입력 가능 수량) */
  INPUT_VALUES: {
    /** 기한 정책 > 소비기한일수/유통기한일수, 판매가능일수, 판매마감일수 */
    POLICY_DAYS: 99999,
    /** 박스당 입수 */
    BOX_PER_QTY: 20000,
  },
  /** 숫자 길이 제한값 (최대 입력 가능 자릿수) */
  INPUT_DIGITS: {
    /** 개당중량(순중량,총중량), 개당부피(길이,높이,폭), 박스당중량(순중량,총중량), 박스당부피(길이,높이,폭) */
    WEIGHT_DIMENSION: 15,
    /** 개당부피 부피, 박스당부피 부피 */
    VOLUME: 17,
    /** 안전재고일수 */
    SAFETY_STOCK_DAYS: 9,
  },
} as const;

/**
 * 기한정책 '관리안함(NONE)' 선택 시 자동 세팅되는 기본값
 */
export const SHELF_LIFE_NONE_DEFAULTS = {
  AVAILABLE_DAYS: 99999,
  SELL_AVAILABLE_DAYS: 99998,
  SELL_DEADLINE_DAYS: 1,
} as const;

/**
 * 상품관리 > 상품 등록/수정 모달의 입력 validation 메세지
 */
export const PRODUCT_VALIDATION_MESSAGE = {
  REQUIRED_INPUT: '필수 입력입니다.',
  REQUIRED_SELECT: '필수 선택입니다.',
  SKU_CODE_SEARCH_NEEDED: '상품코드를 [조회]해주세요.',
  SKU_CODE_INVALID: '등록 가능한 상품코드가 아닙니다.',
  INTEGER_ONLY: '0 이상의 정수만 입력해주세요.',
  POSITIVE_INTEGER_ONLY: '1 이상의 정수만 입력해주세요.',
};

export const PRODUCT_STATUS_BADGE_COLOR = {
  '등록 요청': 'secondary',
  '등록 대기': 'primary',
  '등록 완료': 'info',
} as const;

/**
 * [상품관리] (3PL) 상품 사용여부 옵션
 * @note FBK는 API 호출된 전체 옵션 리스트 사용
 */
export const PRODUCT_USAGE_STATUS = {
  Y: { value: 'Y', text: '사용중' },
  N: { value: 'N', text: '사용중단' },
} as const;

export type TypeProductUsageStatus = (typeof PRODUCT_USAGE_STATUS)[keyof typeof PRODUCT_USAGE_STATUS];
